// /therapy-psychiatry — "Meet our clinical team" surface.
//
// Reworked to sit inside the AumhumSite public shell (same TopBar, Footer,
// scoped tokens, responsive grid) so it reads visually consistent with /, /how-care-works,
// /join etc. Presents the team as *our clinicians*, not a marketplace
// directory — no prices, no ratings, no "starting from" badges.
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { ClubbedAuthModal } from 'src/consumer/components/SignIn/ClubbedAuthComponents'
import { Footer, TopBar } from 'src/consumer/components/AumhumSite/Shell'
import {
  Card,
  Container,
  Eyebrow,
  IconBadge,
  Lede,
  SecondaryButton,
  SectionTitle,
} from 'src/consumer/components/AumhumSite/sections'
import {
  ArrowRight,
  Brain,
  ClipboardList,
  LineChart,
  Microscope,
  Repeat,
  ShieldCheck,
  Stethoscope,
  UserCheck,
  Users,
  Workflow,
} from 'src/consumer/components/AumhumSite/icons'
import 'src/consumer/components/AumhumSite/AumhumSite.css'
import { getPaginatedDataWithAPIString } from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import {
  getArrayOfLanguagesOfMentor,
  getYearsOfExperienceFromProfile,
} from './ServiceProvidersListingComponents'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'

const CANONICAL_ORIGIN = 'https://aumhum.com'

// Preserved for backwards compatibility — src/business/views/TherapyPsychiatryBusiness
// imports this mapping to drive its own filter component.
export const selectedTabApiData = {
  therapist: '/api/external/mentor/1on1/list?categories=therapist',
  psychiatrist: '/api/external/mentor/1on1/list?categories=psychiatrist',
  clinical_psychologist: '/api/external/mentor/1on1/list?categories=clinical_psychologist',
}

// Keep the existing backend shape: one API per specialty, paginated.
const TABS = [
  {
    id: 'therapist',
    label: 'Therapists',
    Icon: Users,
    blurb:
      'Licensed therapists delivering structured therapy — CBT, REBT, trauma-focused, family work — inside a clinical care team.',
    api: '/api/external/mentor/1on1/list?categories=therapist',
    credentialHint: 'Licensed therapist',
    heroLede:
      'Our therapists practice inside a coordinated care team — with supervision, shared clinical records, and easy access to psychiatric and medical collaboration when a case requires it.',
  },
  {
    id: 'psychiatrist',
    label: 'Psychiatrists',
    Icon: Stethoscope,
    blurb:
      'Psychiatric evaluation, medication, and oversight — integrated with your therapy plan rather than handled in isolation.',
    api: '/api/external/mentor/1on1/list?categories=psychiatrist',
    credentialHint: 'Psychiatrist',
    heroLede:
      'Psychiatric care at AUMHUM is integrated with therapy and, where clinically indicated, endocrine review — one care team, one shared clinical record, one continuous plan.',
  },
  {
    id: 'clinical_psychologist',
    label: 'Clinical Psychologists',
    Icon: Brain,
    blurb:
      'Clinical assessment, diagnostic formulation, and structured therapy from MPhil / PhD-level clinicians.',
    api: '/api/external/mentor/1on1/list?categories=clinical_psychologist',
    credentialHint: 'Clinical psychologist',
    heroLede:
      'Our clinical psychologists lead assessment, formulation, and structured therapy — supported by supervision, measurement-based instruments, and the full specialist network when needed.',
  },
]

const normalizeTab = (raw) => TABS.find((t) => t.id === raw) || TABS[0]

// ── Clinician card (authority, not marketplace) ──────────

const initialsFor = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || 'AU'

const ClinicianCard = ({ item, credentialHint }) => {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const name = item?.name || 'AUMHUM clinician'
  const years = getYearsOfExperienceFromProfile(item)
  const languages = getArrayOfLanguagesOfMentor(item, unSecureSetupData) || []
  const focus =
    (Array.isArray(item?.personalize?.focusAreas) && item.personalize.focusAreas) || []
  const bio = item?.personalize?.oneLiner || item?.personalize?.about || ''
  const picUrl = item?.profilePicId ? getImageLink(item.profilePicId) : null

  return (
    <Link
      to={`/mentor/${item?.slug}`}
      className="as-clinician-card"
      aria-label={`View ${name}'s profile`}
    >
      <div className="as-clinician-head">
        <span className="as-clinician-avatar" aria-hidden="true">
          {picUrl ? (
            <img
              src={picUrl}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.textContent = initialsFor(name)
                }
              }}
            />
          ) : (
            initialsFor(name)
          )}
        </span>
        <div>
          <div className="as-clinician-name">{name}</div>
          <div className="as-clinician-creds">
            {credentialHint}
            {years >= 2 ? ` · ${years}+ yrs experience` : ''}
          </div>
        </div>
      </div>
      {focus.length > 0 ? (
        <div className="as-clinician-chips">
          {focus.slice(0, 4).map((f) => (
            <span key={f} className="as-clinician-chip">
              {f}
            </span>
          ))}
        </div>
      ) : null}
      {bio ? <p className="as-clinician-bio">{bio}</p> : null}
      <div className="as-clinician-foot">
        <span className="as-clinician-next">
          {languages.length > 0 ? `Speaks ${languages.slice(0, 3).join(', ')}` : 'Accepting referrals'}
        </span>
        <span className="as-link as-link-violet">
          View profile <ArrowRight />
        </span>
      </div>
    </Link>
  )
}

const Skeletons = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="as-skeleton" />
    ))}
  </>
)

// ── Team listing ─────────────────────────────────────────

const TeamListing = ({ tab }) => {
  const [dataLoading, setDataLoading] = useState(false)
  const [itemsList, setItemsList] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setDataLoading(true)
      setItemsList([])
      setPageNumber(1)
      setHasNextPage(true)
      const resp = await getPaginatedDataWithAPIString(tab.api, true)
      if (cancelled) return
      if (resp?.status === 200) {
        setItemsList(resp?.data || [])
        if (!resp?.data || resp.data.length === 0) setHasNextPage(false)
      } else {
        setHasNextPage(false)
      }
      setDataLoading(false)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [tab.api])

  async function loadMore() {
    const nextPageNumber = pageNumber + 1
    setDataLoading(true)
    const resp = await getPaginatedDataWithAPIString(tab.api, true, nextPageNumber)
    setDataLoading(false)
    if (resp?.status === 200 && resp?.data?.length > 0) {
      setPageNumber(nextPageNumber)
      setItemsList((prev) => [...prev, ...resp.data])
    } else {
      setHasNextPage(false)
    }
  }

  const [sentryRef] = useInfiniteScroll({
    loading: dataLoading,
    hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 200px 0px',
  })

  return (
    <>
      <div className="as-clinicians" aria-busy={dataLoading}>
        {itemsList?.map((item, i) => (
          <ClinicianCard
            key={item?.uid || item?.slug || i}
            item={item}
            credentialHint={tab.credentialHint}
          />
        ))}
        {dataLoading ? <Skeletons count={6} /> : null}
      </div>
      {(hasNextPage || dataLoading) && (
        <div ref={sentryRef} aria-hidden="true" style={{ height: 1 }} />
      )}
      {!dataLoading && itemsList.length === 0 && (
        <Card className="as-mt-8">
          <h3 className="as-h3">No clinicians available right now</h3>
          <p className="as-p as-mt-2">
            Our team is updated continuously. Try another specialty above, or{' '}
            <Link to="/how-care-works" className="as-link as-link-violet">
              see how care works
            </Link>{' '}
            while we match you to the right clinician.
          </p>
        </Card>
      )}
    </>
  )
}

// ── Authority hero ───────────────────────────────────────

const HERO_SIGNALS = [
  { Icon: ShieldCheck, label: 'Licensed clinicians, clinical supervision' },
  { Icon: Workflow, label: 'Integrated care team, shared record' },
  { Icon: LineChart, label: 'Measurement-based, outcome-tracked' },
  { Icon: Repeat, label: 'Continuity across every step' },
]

const Hero = ({ tab }) => (
  <section className="as-hero" aria-labelledby="care-hero-title">
    <Container>
      <div className="as-hero-grid">
        <div>
          <Eyebrow>Our clinical team</Eyebrow>
          <h1 id="care-hero-title" className="as-h1">
            Meet the clinicians delivering your care.
          </h1>
          <p className="as-lede">{tab.heroLede}</p>
          <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
            {/* Hero CTA was previously "How care works" — which sent the
                visitor straight back to /how-care-works, the very page
                they likely arrived from when clicking a "Book Consultation"
                button. That made the whole funnel feel circular. The
                primary action on a clinician directory is to start a
                consultation; "How care works" stays available as a
                secondary link below. */}
            <Link to="/book-consultation" className="as-btn as-btn-primary">
              Book a consultation <ArrowRight />
            </Link>
            <Link to="/how-care-works" className="as-btn as-btn-secondary">
              How care works
            </Link>
          </div>
          <ul className="as-mt-8 as-grid as-grid-2" aria-label="Clinical quality signals">
            {HERO_SIGNALS.map(({ Icon, label }) => (
              <li key={label} className="as-trust-item">
                <Icon /> <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Card>
            <div className="as-flex as-items-center as-gap-3">
              <IconBadge Icon={ClipboardList} />
              <p className="as-h3">How we match you</p>
            </div>
            <ol className="as-pathway-list as-mt-4">
              {[
                { Icon: UserCheck, title: 'Clinical intake', sub: 'A short, confidential conversation about what you are looking for.' },
                { Icon: Brain, title: 'Matching', sub: 'A therapist or psychiatrist aligned to your clinical presentation.' },
                { Icon: Microscope, title: 'Specialists if needed', sub: 'Endocrine or medical review is brought in only when clinically indicated.' },
              ].map(({ Icon, title, sub }) => (
                <li key={title} className="as-pathway-item">
                  <div className="as-icon-circle">
                    <Icon />
                  </div>
                  <div>
                    <p className="as-pathway-item-title">{title}</p>
                    <p className="as-pathway-item-sub">{sub}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="as-pathway-footer">
              <Workflow /> A shared clinical view across every step.
            </div>
          </Card>
        </div>
      </div>
    </Container>
  </section>
)

// ── Head + structured data ───────────────────────────────

const TherapyHelmet = ({ pathname, tab }) => {
  const canonical = `${CANONICAL_ORIGIN}${pathname}`
  const title = `${tab.label} at AUMHUM — clinical mental healthcare`
  const description = tab.blurb
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: 'AUMHUM',
      url: CANONICAL_ORIGIN,
      logo: APP_CONSTANTS.APP_LOGO_LINK,
      medicalSpecialty: tab.id === 'psychiatrist' ? 'Psychiatry' : 'Psychology',
      areaServed: ['IN', 'US', 'AE'],
      availableLanguage: ['en', 'hi'],
    }),
    [tab.id],
  )
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <meta property="og:site_name" content="AUMHUM" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={APP_CONSTANTS.APP_LOGO_LINK} />
      <meta property="og:locale" content="en_IN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={APP_CONSTANTS.APP_LOGO_LINK} />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// ── Root ──────────────────────────────────────────────────

export default function TherapistPsychiatristListingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [authOpen, setAuthOpen] = useState(false)

  const activeTab = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return normalizeTab(params.get('t'))
  }, [location.search])

  const selectTab = (id) => {
    if (id === activeTab.id) return
    navigate(`${location.pathname}?t=${id}`, { replace: false })
  }

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
  }, [activeTab.id])

  return (
    <div className="aumhum-site-root">
      <TherapyHelmet pathname={location.pathname} tab={activeTab} />
      <a href="#main" className="as-skip-link">
        Skip to content
      </a>
      <TopBar onLogin={() => setAuthOpen(true)} />
      <main id="main">
        <Hero tab={activeTab} />

        <section className="as-section as-section-white" aria-labelledby="team-heading">
          <Container>
            <div className="as-max-w-3">
              <Eyebrow>Choose a specialty</Eyebrow>
              <SectionTitle>
                <span id="team-heading">Our clinical team — by specialty</span>
              </SectionTitle>
              <Lede>
                Every clinician on AUMHUM is vetted, credentialled, and practices inside a
                coordinated clinical system. Pick a specialty to see who is available, read
                their focus, and start a clinical intake.
              </Lede>
            </div>

            <div
              className="as-tabs as-mt-8"
              role="tablist"
              aria-label="Clinician specialty"
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={t.id === activeTab.id}
                  className={`as-tab ${t.id === activeTab.id ? 'as-tab--active' : ''}`}
                  onClick={() => selectTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="as-p as-mt-4">{activeTab.blurb}</p>

            <TeamListing tab={activeTab} />
          </Container>
        </section>

        <section className="as-section as-section-alt">
          <Container>
            <div className="as-grid-12 as-items-center">
              <div className="as-col-8">
                <Eyebrow>Still deciding?</Eyebrow>
                <SectionTitle>Not sure which clinician is the right fit?</SectionTitle>
                <Lede>
                  Start with a clinical intake. Our team will help you understand whether
                  therapy, psychiatric review, or a combination is appropriate — and match you
                  to the right clinician.
                </Lede>
              </div>
              <div className="as-col-4 as-flex as-wrap as-gap-3 as-justify-end">
                {/* Used to open the auth modal directly, which conflated
                    "I want to book" with "I want to log in". Send the
                    visitor to /book-consultation instead — that page asks
                    the new-vs-returning question and then routes to the
                    right next step. */}
                <Link to="/book-consultation" className="as-btn as-btn-primary">
                  Start a consultation <ArrowRight />
                </Link>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
      <ClubbedAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        loginVariant={false}
      />
    </div>
  )
}
